import apiClient from '@/lib/api';

/** Emails run AI calls; the API stops them itself before 120 s. */
const EMAIL_TIMEOUT_MS = 130_000;
/** Scans fetch up to three pages, then run the AI; the API allows them 180 s. */
const SCAN_TIMEOUT_MS = 190_000;

export interface BusinessSummary {
  summary: string;
  shortSummary: string;
  businessName: string | null;
  businessType: string | null;
  niche: string | null;
  offer: string | null;
  targetCustomer: string | null;
  location: string | null;
}

export interface ScannedPage {
  url: string;
  ok: boolean;
  chars: number;
  error?: string;
}

export interface SavedScan {
  url: string;
  status: string;
  scannedAt: string | null;
  summary: BusinessSummary | null;
  pages: ScannedPage[];
  error: string | null;
}

export interface PreviewPage {
  url: string;
  subaccount: { name: string; isMain: boolean } | null;
  forms: string[];
  saved: SavedScan | null;
}

export interface PreviewAccount {
  agency: { publicId: string; name: string; createdAt: string };
  owner: { firstName: string | null; email: string } | null;
  plan: string;
  billing: string;
  stage: string;
  trialRisk: string;
  trial: { startsAt: string | null; endsAt: string | null };
  forms: { total: number; embedded: number };
  main: { name: string; subaccounts: number } | null;
  inPilot: boolean;
  inGhl: boolean;
  company: { domain: string | null; saved: SavedScan | null };
  pages: PreviewPage[];
}

export type ScanResult = {
  kind: 'company' | 'funnel';
  url: string;
  scannedAt: string;
  pages: ScannedPage[];
} & ({ status: 'done'; summary: BusinessSummary } | { status: 'failed'; errorCode: string; errorMessage: string });

export interface PreviewScan {
  url: string;
  scannedAt: string;
  summary: BusinessSummary;
}

export interface PreviewScans {
  company: PreviewScan | null;
  funnels: PreviewScan[];
}

export interface PushRow {
  name: string;
  current: string | null;
  next: string;
  change: 'new' | 'changed' | 'same' | 'cleared';
}

export interface PushPreview {
  ghl: 'found' | 'missing' | 'excluded' | 'off' | 'unreachable';
  contact: { email: string | null; firstName: string | null } | null;
  fields: PushRow[];
  notes: Array<{ body: string; state: 'new' | 'added_before' }>;
  tags: Array<{ tag: string; state: 'new' | 'on_contact' | 'sent_before' }>;
}

export interface Guard {
  key: string;
  label: string;
  ok: boolean;
  detail: string;
}

export interface EmailPreview {
  picked: string | null;
  emailKey: string | null;
  facts: { hasForm?: boolean; hasEmbed?: boolean };
  guards: Guard[];
  wouldSend: boolean;
  fieldsFrom: 'ghl' | 'database';
  outOfDate: Array<{ name: string; ghl: string; now: string }>;
  to: string | null;
  bookingUrl: string | null;
  email: { subject: string; body: string; problems: string[]; attempts: number; model: string } | null;
}

export interface AiEmailInfo {
  key: string;
  name: string;
  audience: string;
}

export interface PromptParts {
  style: string;
  subject: string;
  emails: Record<string, { where: string; offer: string }>;
}

export interface PromptsOverview {
  emails: AiEmailInfo[];
  defaults: PromptParts;
  saved: Array<{ key: string; text: string; updatedAt: string; updatedBy: string | null }>;
  fields: string[];
}

const base = (publicId: string) => `/admin/ai-preview/agencies/${encodeURIComponent(publicId)}`;

export const aiPreviewService = {
  getAccount: async (publicId: string): Promise<PreviewAccount> => (await apiClient.get(base(publicId))).data.data,

  scan: async (publicId: string, target: { kind: 'company' } | { kind: 'funnel'; url: string }): Promise<ScanResult> =>
    (await apiClient.post(`${base(publicId)}/scan`, target, { timeout: SCAN_TIMEOUT_MS })).data.data,

  push: async (publicId: string, scans: PreviewScans): Promise<PushPreview> =>
    (await apiClient.post(`${base(publicId)}/push`, { scans })).data.data,

  email: async (
    publicId: string,
    input: { scans: PreviewScans; emailKey?: string; wording?: Record<string, string> },
  ): Promise<EmailPreview> => (await apiClient.post(`${base(publicId)}/email`, input, { timeout: EMAIL_TIMEOUT_MS })).data.data,

  getPrompts: async (): Promise<PromptsOverview> => (await apiClient.get('/admin/ai-preview/prompts')).data.data,

  saveWording: async (key: string, text: string): Promise<PromptsOverview> =>
    (await apiClient.put('/admin/ai-preview/prompts', { key, text })).data.data,

  resetWording: async (key: string): Promise<PromptsOverview> =>
    (await apiClient.delete(`/admin/ai-preview/prompts?key=${encodeURIComponent(key)}`)).data.data,
};
